import { IOsiOsvWidgetViewModel, IOsiOsvWidgetViewModelToken } from '@shared/types/mobx/widgets/OsiOsvWidget';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { darken, lighten } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { tokens, TranslatedToken, useTranslation } from '@shared/utils/i18n';
import { flatComparator } from '@shared/utils/helpers/flatComparator';

import { Box, Button, Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/lab';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { OsvGrid } from '@shared/common/OsvGrid';
import { GridValueFormatterParams } from '@mui/x-data-grid-pro';
import { formatOsvMoney } from '@shared/utils/helpers/osvMoney';
// OSI-808: общие минимальные ширины колонок ОСВ (обоснование чисел — в самом файле)
import { OSV_MONEY_COL_MIN_WIDTH, OSV_TITLE_COL_MIN_WIDTH } from '@shared/utils/helpers/osvColumnWidths';
// OSI-788: перевод признака владельца для отображения + проверка «значение пусто»
import { isOwnerEmpty, translateOwner } from '@shared/utils/helpers/ownerType';

const getBackgroundColor = (color: any, mode: any) => (mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6));

const getHoverBackgroundColor = (color: any, mode: any) =>
  `${mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5)} !important`;

export const OsiOsvWidget: React.FC = observer(() => {
  const vm = useInjection<IOsiOsvWidgetViewModel>(IOsiOsvWidgetViewModelToken);

  const { translateToken: tt, t } = useTranslation();

  // Строки грида берём один раз: геттер sortedAbonents на каждом обращении создаёт новый массив.
  const rows = vm.sortedAbonents;

  // OSI-788: колонка «Владелец» скрыта по умолчанию, ТОЛЬКО если значение пусто у ВСЕХ строк.
  // ВАЖНО: в ОСВ дерево двухуровневое (см. getOsvRows в model/ViewModel.ts): строка помещения
  // получает level: 0 и поле owner, а вложенные строки-услуги — level: 1 и поля owner не имеют
  // вовсе (объект услуги собирается из ...service). Уровень строки определяем именно по полю level —
  // это же поле грид использует в getRowClassName внутри OsvGrid. Проверяем строго level === 0:
  // «Владелец» — атрибут помещения, а не услуги, поэтому строки услуг в выборку попадать не должны.
  const hasOwner = rows.some((row: any) => row.level === 0 && !isOwnerEmpty(row.owner));

  // Видимость колонок контролируем локально и прокидываем в общий OsvGrid.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({ owner: false });

  // OSI-788: признак ручного переключения колонки «Владелец» через штатное меню MUI «Столбцы».
  // После ручного выбора автоматика видимости для owner больше не применяется — иначе колонка,
  // включённая пользователем, пропадала бы при следующем пересчёте строк (смена месяца,
  // «Обновить», чекбокс сворачивания дочерних помещений, смена языка).
  const isOwnerVisibilityManualRef = useRef(false);

  useEffect(() => {
    if (isOwnerVisibilityManualRef.current) return;

    setColumnVisibilityModel((prev) => ({ ...prev, owner: hasOwner }));
  }, [hasOwner]);

  const handleColumnVisibilityModelChange = (model: Record<string, boolean>) => {
    // В модели MUI отсутствие поля означает «колонка видима», поэтому сравниваем через !== false.
    const wasVisible = columnVisibilityModel.owner !== false;
    const willBeVisible = model.owner !== false;

    if (wasVisible !== willBeVisible) {
      isOwnerVisibilityManualRef.current = true;
    }

    setColumnVisibilityModel(model);
  };

  const columns = useMemo(
    () => [
      {
        field: 'flat',
        headerName: tt(tokens.osiOSV.table.number),
        // OSI-808: 160 -> 240, иначе названия категорий услуг обрезались («Текущие взносы н…»)
        minWidth: OSV_TITLE_COL_MIN_WIDTH,
        valueGetter: ({ row }: { row: any }) => {
          const { flat } = row;
          return flat[flat.length - 1];
        },
        sortComparator: (v1: any, v2: any) => {
          if (v1.length < 6) {
            return flatComparator(v1, v2);
          }
          return true;
        },
        flex: 0.3
      },
      {
        field: 'abonentName',
        headerName: tt(tokens.osiOSV.table.abonentName),
        minWidth: 80,
        flex: 0.3
      },
      {
        field: 'owner',
        headerName: tt(tokens.osiOSV.table.owner),
        minWidth: 80,
        flex: 0.2,
        // OSI-788: перевод значения только для отображения (valueFormatter, не valueGetter).
        // В «Сальдо» колонка ТОЛЬКО ДЛЯ ЧТЕНИЯ (editable не выставляем) — редактирование
        // признака владельца доступно исключительно в разделе «Помещения/Квартиры».
        // У вложенных строк-услуг (level 1) поля owner нет — форматтер вернёт пустую строку.
        valueFormatter: ({ value }: GridValueFormatterParams) => translateOwner(value, t)
      },
      {
        field: 'totalBegin',
        headerName: tt(tokens.osiOSV.table.totalBegin),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        // OSI-808: 50 -> 125, иначе крупные суммы обрезались («65 270 007…»); константа общая для всех экранов ОСВ
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        // Форматтер денег: округляет до 2 знаков и убирает артефакт «-0» при свёрнутых дочерних помещениях
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalDebet',
        headerName: tt(tokens.osiOSV.table.totalDebt),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalFixes',
        headerName: tt(tokens.osiOSV.table.totalFixes),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalKredit',
        headerName: tt(tokens.osiOSV.table.totalKredit),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalFine',
        headerName: tt(tokens.osiOSV.table.totalFine),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      },
      {
        field: 'totalEnd',
        headerName: tt(tokens.osiOSV.table.totalEnd),
        headerAlign: 'left',
        type: 'number',
        align: 'left',
        minWidth: OSV_MONEY_COL_MIN_WIDTH,
        flex: 0.2,
        valueFormatter: ({ value }: GridValueFormatterParams) => formatOsvMoney(value)
      }
    ],
    // OSI-788: t добавлен в зависимости — от него зависит перевод значения колонки «Владелец»
    [tt, t]
  );

  return (
    <>
      <Box display="flex" alignItems="center">
        <Grid container spacing={1}>
          <Grid item xs={6} sm={2}>
            <DatePicker
              views={['year', 'month']}
              label={tt(tokens.common.formFields.month.label)}
              minDate={new Date('2021-03-01')}
              maxDate={new Date('2031-03-01')}
              value={vm.dateFieldValue}
              openTo={'month'}
              onAccept={async (date: Date | null) => {
                vm.dateFieldValue = date;
                await vm.loadOsv();
              }}
              renderInput={(params) => <TextField size="small" {...params} helperText={null} />}
              onChange={(date: Date | null) => {
                vm.dateFieldValue = date;
              }}
              disabled={vm.isLoading}
            />
          </Grid>
          {/* Чекбокс перенесён вправо от кнопки «Скачать» — теперь он внутри Box с кнопками, после кнопки скачивания */}
          <Grid item xs={12} sm={7}>
            <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={vm.loadOsv}
                size="medium"
                disabled={vm.isLoading}
              >
                <TranslatedToken id={tokens.common.refresh} />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudDownloadIcon />}
                onClick={vm.downloadOsv}
                sx={{ ml: 1 }}
                size="medium"
                disabled={vm.isLoading}
              >
                <TranslatedToken id={tokens.common.download} />
              </Button>
              {/* Чекбокс «Свернуть дочерние помещения» — схлопывает паркинги/кладовки в родителя; размещён правее кнопки «Скачать».
                  OSI-806: показываем ТОЛЬКО если у ОСИ реально есть дочерние помещения (vm.hasChildAbonents,
                  флаг приходит с бэка в osiInfo). В домах без паркингов/кладовок чекбокс бессмыслен.
                  Значение vm.isCollapseChildren при этом НЕ сбрасываем — сворачивать всё равно нечего. */}
              {vm.hasChildAbonents && (
                <FormControlLabel
                  sx={{ ml: 1 }}
                  control={
                    <Checkbox
                      checked={vm.isCollapseChildren}
                      onChange={(e) => vm.setCollapseChildren(e.target.checked)}
                      disabled={vm.isLoading}
                    />
                  }
                  label={tt(tokens.osiOSV.collapseChildren)}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          height: '60vh',
          '& .row-child--level-1': {
            bgcolor: (theme) => getBackgroundColor(theme.palette.info.main, theme.palette.mode),
            '&:hover': {
              bgcolor: (theme) => getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode)
            }
          }
        }}
      >
        <OsvGrid
          isLoading={vm.isLoading}
          columns={columns}
          rows={rows}
          totals={vm.totalsOsv}
          rowCount={vm.abonentsCount}
          getTreeDataPath={(row) => row.id}
          // OSI-808: закреплённые снизу строки итогов («ИТОГО» + категории услуг).
          // Массив готовит ViewModel в rebuildRows — по тем же строкам, что и грид,
          // поэтому свёрнутые дочерние помещения, месяц и язык учитываются автоматически.
          totalRows={vm.osvTotalRows}
          // OSI-788: условная видимость колонки «Владелец» (только на этом экране)
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        />
      </Box>
    </>
  );
});
